// Presets — the Agreements page's six saved views, restored (sol finding 17).
//
// The deleted `AgreementsPage` opened on a row of URL-backed presets: All
// active · Out for signature · Waiting on WHP · Blocked · Renewals · Never
// sent. The replacement kept the three VIEWS and the individual filters but
// not the one-click combinations, so "show me everything that was generated
// and never sent" became: switch view, open the state select, find the right
// state, hope it is the one that means that.
//
// Two rules carried over from the page that had them, because both are what
// made the old row trustworthy:
//
//   • A PRESET IS A SHORTCUT, NOT STATE. Each one writes the ORDINARY filters
//     into the URL — `view`, `party`, `blocked`, `renewals`, `types`, `state`
//     — and nothing else. There is no `preset=` param, nothing server-side
//     knows about them, and a preset's URL is one an operator could have built
//     by hand. Paste it to someone without this row and it still works.
//
//   • THE ACTIVE PRESET IS DERIVED, NEVER REMEMBERED. A preset lights up iff
//     the current filters exactly match it, so a hand-edited URL lights the
//     right one and removing a single token honestly un-lights it. A stored
//     "which preset did they click" would go on claiming a view the URL no
//     longer describes.
//
// Clicking REPLACES the filters and keeps `q`: the operator's search is what
// they typed, and silently discarding it is the one thing a "saved view"
// must not do.

import type { SentItemsListParams } from '@/lib/sentItemsClient';

/** The filter dimensions a preset owns. `q`, paging and `scope` are not its
 *  business — see the header. */
export type PresetParams = Pick<
  SentItemsListParams,
  'view' | 'types' | 'renewalsOnly' | 'state' | 'step' | 'party' | 'blocked'
>;

export interface SentItemsPreset {
  id: string;
  label: string;
  /** Why this set, in the words the operator would use. */
  title: string;
  params: PresetParams;
}

/**
 * The six, in the order the Agreements page listed them.
 *
 * Every `params` here is expressed in filters that already existed — the whole
 * point of the finding is that the combinations were lost, not the filters.
 *
 * On two of them the mapping is worth stating out loud:
 *
 *   Out for signature / Waiting on WHP → `waitingOn.party`, not a phase. That
 *   is the question actually being asked (is this ours to move, or theirs) and
 *   it answers correctly for a draft, which no phase filter did. It is also
 *   the same translation §6.2 already made for the two shortcut buttons.
 *
 *   Never sent → `state: 'draft'`. A lease that was generated and never sent
 *   and a lease still being typed are both `draft` — `stateLabel` is what
 *   separates them ("Generated, not sent" vs "Being prepared"), and `draft` is
 *   the state that holds every item nothing has ever gone out for.
 */
export const SENT_ITEMS_PRESETS: readonly SentItemsPreset[] = [
  {
    id: 'all-active',
    label: 'All active',
    title: 'Everything still in motion, most recently touched first.',
    params: { view: 'recent' },
  },
  {
    id: 'out',
    label: 'Out for signature',
    title: 'With a resident, applicant, landlord or owner right now.',
    params: { view: 'waiting', party: 'recipient' },
  },
  {
    id: 'waiting-whp',
    label: 'Waiting on WHP',
    title: 'Ours to move: countersignatures, drafts, anything held here.',
    params: { view: 'waiting', party: 'whp' },
  },
  {
    id: 'blocked',
    label: 'Blocked',
    title: 'Something must be fixed before it moves.',
    params: { view: 'all', blocked: true },
  },
  {
    id: 'renewals',
    label: 'Renewals',
    title: 'Leases being renewed for a tenancy already in place.',
    params: { view: 'all', types: ['lease'], renewalsOnly: true },
  },
  {
    id: 'never-sent',
    label: 'Never sent',
    title: 'Prepared or generated, and never sent to anybody.',
    params: { view: 'all', state: 'draft' },
  },
];

/** A preset's params, normalised for comparison — every dimension stated. */
function normalise(params: PresetParams): string {
  return JSON.stringify({
    view: params.view ?? 'all',
    types: [...(params.types ?? [])].sort(),
    renewalsOnly: params.renewalsOnly === true,
    state: params.state ?? null,
    step: params.step ?? null,
    party: params.party ?? null,
    blocked: params.blocked === true,
  });
}

/**
 * Which preset the current filters ARE, or null.
 *
 * EXACT: adding a type chip to "Waiting on WHP" leaves no preset lit, which is
 * the truth — the set on screen is no longer that saved view. `q`, the date
 * range, paging and `scope` are ignored because no preset claims them.
 */
export function activePresetId(params: SentItemsListParams): string | null {
  const want = normalise(params);
  return SENT_ITEMS_PRESETS.find((p) => normalise(p.params) === want)?.id ?? null;
}

/**
 * The full params a preset click produces, given what is on screen now.
 *
 * Everything the preset owns is REPLACED (an unset dimension is explicitly
 * cleared, which is what `SentItemsFilterPatch` exists for); `q` rides along.
 */
export function paramsForPreset(
  preset: SentItemsPreset,
  current: SentItemsListParams,
): SentItemsListParams {
  const next: SentItemsListParams = { view: preset.params.view ?? 'all' };
  if (preset.params.types?.length) next.types = [...preset.params.types];
  if (preset.params.renewalsOnly) next.renewalsOnly = true;
  if (preset.params.state) next.state = preset.params.state;
  if (preset.params.step) next.step = preset.params.step;
  if (preset.params.party) next.party = preset.params.party;
  if (preset.params.blocked) next.blocked = true;
  if (current.q?.trim()) next.q = current.q;
  return next;
}

export function Presets({
  params,
  onSelect,
}: {
  params: SentItemsListParams;
  /** The whole next param set — the page writes it to the URL. */
  onSelect: (next: SentItemsListParams) => void;
}): React.JSX.Element {
  const active = activePresetId(params);
  return (
    <div className="si-presets" role="group" aria-label="Saved views">
      {SENT_ITEMS_PRESETS.map((preset) => {
        const on = preset.id === active;
        return (
          <button
            key={preset.id}
            type="button"
            className="btn"
            data-variant={on ? 'secondary' : 'ghost'}
            data-size="sm"
            data-preset={preset.id}
            aria-pressed={on}
            title={preset.title}
            onClick={() => onSelect(paramsForPreset(preset, params))}
          >
            {preset.label}
          </button>
        );
      })}
    </div>
  );
}
